import { isPortal } from "./portal";
import { detectStore } from "./removeAds";
import { hasPlayedDaily } from "./redux/daily";

// Achievements in the apps: Google Play Games Services v2 on Android, Game Center on iPhone.
//
// Android: a small native plugin, "PlayGames" (app/android/.../PlayGamesPlugin.java), starts the
// Play Games SDK and unlocks achievements. Play Games v2 signs players in automatically when they
// have a Play Games profile; anyone not signed in simply gets no achievements.
// iPhone: the "GameCenter" plugin (app/plugins/rafiki-game-center) signs the player in to Game
// Center quietly (Apple shows its own banner) and reports achievements by percentage. Apple has no
// "increment", so Card shark's progress is counted on the device (localStorage) and sent as a %.
//
// Everything here does nothing on the website, the portals, Amazon Fire tablets (no Google Play
// services) and while PLAY_GAMES_ENABLED is false. Switched on in 1.7.1 (4 Nov 2026), after the
// Play Games project was published.
export const PLAY_GAMES_ENABLED = true;

// Achievement IDs from Play Console (Play Games Services project 201918875792).
export const ACHIEVEMENTS = {
  firstHand: "CgkIkJG2mvAFEAIQAQ", // finish your first game
  slam: "CgkIkJG2mvAFEAIQAg", // first correct slam
  lowestWins: "CgkIkJG2mvAFEAIQAw", // win a round
  rafiki: "CgkIkJG2mvAFEAIQBA", // call Rafiki and win the round
  dailyDealer: "CgkIkJG2mvAFEAIQBQ", // finish a Daily Rafiki
  blindFaith: "CgkIkJG2mvAFEAIQBg", // win a round after making a blind swap
  belowZero: "CgkIkJG2mvAFEAIQBw", // finish a round with a score of 0 or less
  holidayHabit: "CgkIkJG2mvAFEAIQCA", // Daily Rafiki 3 days in a row
  weekAway: "CgkIkJG2mvAFEAIQCQ", // Daily Rafiki 7 days in a row
  cardShark: "CgkIkJG2mvAFEAIQCg", // win 25 rounds (incremental, 25 steps)
};
export const CARD_SHARK_STEPS = 25;

// The same achievements in App Store Connect (Game Center), by Apple "vendor identifier".
export const GAME_CENTER_IDS: Record<string, string> = {
  [ACHIEVEMENTS.firstHand]: "rafiki.first_hand",
  [ACHIEVEMENTS.slam]: "rafiki.slam",
  [ACHIEVEMENTS.lowestWins]: "rafiki.lowest_wins",
  [ACHIEVEMENTS.rafiki]: "rafiki.rafiki",
  [ACHIEVEMENTS.dailyDealer]: "rafiki.daily_dealer",
  [ACHIEVEMENTS.blindFaith]: "rafiki.blind_faith",
  [ACHIEVEMENTS.belowZero]: "rafiki.below_zero",
  [ACHIEVEMENTS.holidayHabit]: "rafiki.holiday_habit",
  [ACHIEVEMENTS.weekAway]: "rafiki.week_away",
  [ACHIEVEMENTS.cardShark]: "rafiki.card_shark",
};

export type AchievementOp = { kind: "unlock"; id: string } | { kind: "increment"; id: string; steps: number };

const unlock = (id: string): AchievementOp => ({ kind: "unlock", id });

// ---- What each game event earns (pure; tested in playGames.test.js) ----

export interface RoundFacts {
  won: boolean; // the player is among the round's winners (lowest score; ties count)
  called: boolean; // the player called Rafiki this round
  roundScore?: number; // the player's score for the round
  blindSwap: boolean; // the player made a blind swap this round
}

export const achievementsForRound = (f: RoundFacts): AchievementOp[] => {
  const ops: AchievementOp[] = [];
  if (f.won) {
    ops.push(unlock(ACHIEVEMENTS.lowestWins));
    ops.push({ kind: "increment", id: ACHIEVEMENTS.cardShark, steps: 1 });
    if (f.called) ops.push(unlock(ACHIEVEMENTS.rafiki));
    if (f.blindSwap) ops.push(unlock(ACHIEVEMENTS.blindFaith));
  }
  if (typeof f.roundScore === "number" && f.roundScore <= 0) ops.push(unlock(ACHIEVEMENTS.belowZero));
  return ops;
};

export const achievementsForGameOver = (): AchievementOp[] => [unlock(ACHIEVEMENTS.firstHand)];

export const achievementsForCorrectSlam = (): AchievementOp[] => [unlock(ACHIEVEMENTS.slam)];

const DAY_MS = 24 * 60 * 60 * 1000;
const shiftDate = (date: string, days: number) => new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

// Consecutive Daily Rafikis (UTC days, as Daily Rafiki counts them) ending on `date`.
export const dailyRunEndingOn = (date: string, played: (date: string) => boolean, max = 30): number => {
  let n = 0;
  while (n < max && played(shiftDate(date, -n))) n += 1;
  return n;
};

// `played` says whether this device has a Daily Rafiki result for a date (redux/daily.ts).
export const achievementsForDaily = (date: string, played: (date: string) => boolean): AchievementOp[] => {
  const ops: AchievementOp[] = [unlock(ACHIEVEMENTS.dailyDealer)];
  const run = dailyRunEndingOn(date, (d) => d === date || played(d));
  if (run >= 3) ops.push(unlock(ACHIEVEMENTS.holidayHabit));
  if (run >= 7) ops.push(unlock(ACHIEVEMENTS.weekAway));
  return ops;
};

// ---- Native side ----

let enabledForTests: boolean | undefined;
// Tests only: the flag is a constant, so the tests switch it here.
export const _setPlayGamesEnabledForTests = (on: boolean | undefined) => {
  enabledForTests = on;
  started = null;
  seenRounds.clear();
  seenGames.clear();
  blindSwapRound = undefined;
};
const enabled = () => (enabledForTests === undefined ? PLAY_GAMES_ENABLED : enabledForTests);

const capacitor = (): any => (typeof window !== "undefined" ? (window as any).Capacitor : undefined);
type AppPlatform = "android" | "ios" | null;
const appPlatform = (): AppPlatform => {
  const cap = capacitor();
  if (!cap?.isNativePlatform?.()) return null;
  const p = cap?.getPlatform?.();
  return p === "android" || p === "ios" ? p : null;
};
const plugin = (): any => {
  try {
    const cap = capacitor();
    const name = appPlatform() === "ios" ? "GameCenter" : "PlayGames";
    return cap?.Plugins?.[name] || cap?.registerPlugin?.(name);
  } catch (e) {
    return undefined;
  }
};

export const playGamesAllowed = (): boolean => enabled() && !isPortal && appPlatform() !== null && Boolean(plugin());

// Card shark progress on iPhone (Game Center takes a percentage, not steps).
const CARD_SHARK_KEY = "rafiki_card_shark_wins";
export const cardSharkPercent = (wins: number) => Math.min(100, Math.round((Math.max(0, wins) / CARD_SHARK_STEPS) * 100));
const bumpCardShark = (steps: number): number => {
  let n = 0;
  try {
    n = (Number(window.localStorage.getItem(CARD_SHARK_KEY)) || 0) + steps;
    window.localStorage.setItem(CARD_SHARK_KEY, String(n));
  } catch (e) {}
  return n;
};

let started: Promise<boolean> | null = null;

// Start the Play Games SDK (once, at app start). Resolves true when the player is signed in.
// Never throws: a device without Google Play services just has no achievements.
export const initPlayGames = (): Promise<boolean> => {
  if (!playGamesAllowed()) return Promise.resolve(false);
  if (started) return started;
  started = (async () => {
    try {
      // Fire tablets have no Google Play services.
      if (appPlatform() === "android" && (await detectStore()) === "amazon") return false;
      const res = await plugin().initialize();
      return Boolean(res?.authenticated);
    } catch (e) {
      console.warn("Play Games failed to start", e);
      return false;
    }
  })();
  return started;
};

const report = async (ops: AchievementOp[]) => {
  if (!ops.length || !playGamesAllowed()) return;
  try {
    if (!(await initPlayGames())) return; // not signed in: nothing to report to
    const p = plugin();
    const ios = appPlatform() === "ios";
    for (const op of ops) {
      try {
        if (ios) {
          const id = GAME_CENTER_IDS[op.id];
          if (!id) continue;
          const percent = op.kind === "unlock" ? 100 : cardSharkPercent(bumpCardShark(op.steps));
          await p.report({ id, percent });
        } else if (op.kind === "unlock") await p.unlock({ id: op.id });
        else await p.increment({ id: op.id, steps: op.steps });
      } catch (e) {
        // one failed achievement mustn't stop the others
      }
    }
  } catch (e) {}
};

// Blind swaps are not in the round's saved data, so the swap is noted when the player makes it
// (redux/actions.ts swapCards) against the round it was made in.
let blindSwapRound: string | undefined;
export const noteBlindSwap = (roundId?: string) => {
  if (roundId) blindSwapRound = roundId;
};

// Each round and game is reported once, even if the score sheet is opened again.
const seenRounds = new Set<string>();
const seenGames = new Set<string>();

export const onRoundOverAchievements = (roundId: string | undefined, facts: Omit<RoundFacts, "blindSwap">) => {
  if (!playGamesAllowed() || !roundId || seenRounds.has(roundId)) return;
  seenRounds.add(roundId);
  report(achievementsForRound({ ...facts, blindSwap: blindSwapRound === roundId }));
};

export const onGameOverAchievements = (gameId: string | undefined) => {
  if (!playGamesAllowed() || !gameId || seenGames.has(gameId)) return;
  seenGames.add(gameId);
  report(achievementsForGameOver());
};

export const onCorrectSlamAchievement = () => {
  if (!playGamesAllowed()) return;
  report(achievementsForCorrectSlam());
};

// Called once per day when the Daily Rafiki result is first saved (GameOverActions).
export const onDailyCompleteAchievements = (date: string) => {
  if (!playGamesAllowed()) return;
  report(achievementsForDaily(date, hasPlayedDaily));
};

// The platform's own achievements screen (not linked from the game yet).
export const showAchievements = async () => {
  if (!playGamesAllowed()) return;
  try {
    await plugin().showAchievements();
  } catch (e) {}
};
